import React, { useState, useMemo, useEffect, useRef } from 'react';
import { X, Save, RefreshCw, Plus, Trash2, CheckSquare, Square } from 'lucide-react';
import { useData } from '../context/DataContext';
import { useT } from '../context/LanguageContext';
import { carsysApi } from '../api/carsysApi';
import { toast } from 'sonner';
import { makeDirtyClose } from '../lib/useDirtyClose';
import ScanAutopapieren from './ScanAutopapieren';

const STATUSSEN = ['Voorraad', 'Reservering', 'Verkocht', 'Overschreven', 'Geschorst', 'Gearchiveerd'];

const DEFAULT_TITEL_CHECKLIST = [
  { key: 'vrijwaring_in', label: 'Vrijwaringsbewijs ontvangen bij inkoop', done: false },
  { key: 'handelsvoorraad', label: 'Op handelsvoorraad gezet (RDW)', done: false },
  { key: 'kentekencard', label: 'Kentekencard + tenaamstellingscode aanwezig', done: false },
  { key: 'apk', label: 'APK geldig / gekeurd', done: false },
  { key: 'nap', label: 'Tellerstand logisch (NAP-controle)', done: false },
  { key: 'bpm', label: 'BPM / rest-BPM gecontroleerd (bij import)', done: false },
  { key: 'tenaamstelling_uit', label: 'Tenaamstelling nieuwe eigenaar bij verkoop', done: false },
  { key: 'vrijwaring_uit', label: 'Vrijwaringsbewijs aan koper / overschrijving', done: false },
];

const newKostenpost = () => ({
  id: `k${Math.random().toString(36).slice(2, 8)}`,
  omschrijving: '',
  kosten: 0,
  voorgeschotenDoor: '',
  datum: new Date().toISOString().slice(0, 10),
});

export default function HandelAutoEditor({ auto, onClose }) {
  const { t } = useT();
  const { createHandelAuto, updateHandelAuto, rdwLookup, vinLookup } = useData();
  const isNew = !auto || !auto.id;
  const [team, setTeam] = useState([]);
  useEffect(() => {
    carsysApi.getTeam().then(setTeam).catch(() => {});
  }, []);
  const [data, setData] = useState(() => auto || {
    kenteken: '', merk: '', model: '', bouwjaar: '',
    kmStand: 0, inkoopPrijs: 0, verkoopPrijs: 0,
    status: 'Voorraad', notitie: '',
    ingekochtOp: new Date().toISOString().slice(0, 10),
    kostenposten: [],
    chassisnummer: '',
  });
  const [loadingRdw, setLoadingRdw] = useState(false);
  const [loadingVin, setLoadingVin] = useState(false);
  const initialSig = useRef(null);
  if (initialSig.current === null) initialSig.current = JSON.stringify(data);
  const requestClose = makeDirtyClose(() => JSON.stringify(data) !== initialSig.current, onClose, t);

  const upd = (k, v) => setData(d => ({ ...d, [k]: v }));

  const applyScan = (r) => setData(d => ({
    ...d,
    kenteken: d.kenteken || (r.kenteken || ''),
    voertuigType: r.voertuigType || d.voertuigType,
    merk: r.merk || d.merk,
    model: r.model || d.model,
    bouwjaar: r.bouwjaar || d.bouwjaar,
    chassisnummer: r.chassisnummer || d.chassisnummer,
  }));

  const kostenposten = useMemo(() => data.kostenposten || [], [data.kostenposten]);
  const addKostenpost = () => upd('kostenposten', [...kostenposten, newKostenpost()]);
  const updateKostenpost = (id, patch) =>
    upd('kostenposten', kostenposten.map(k => k.id === id ? { ...k, ...patch } : k));
  const removeKostenpost = (id) =>
    upd('kostenposten', kostenposten.filter(k => k.id !== id));

  const titelChecklist = (data.titelChecklist && data.titelChecklist.length) ? data.titelChecklist : DEFAULT_TITEL_CHECKLIST;
  const toggleChecklist = (key) => upd('titelChecklist', titelChecklist.map(c => c.key === key ? { ...c, done: !c.done } : c));

  const totaalKosten = useMemo(
    () => kostenposten.reduce((s, k) => s + (Number(k.kosten) || 0), 0),
    [kostenposten]
  );
  const marge = (Number(data.verkoopPrijs) || 0) - (Number(data.inkoopPrijs) || 0) - totaalKosten;
  const margePct = data.inkoopPrijs > 0 ? Math.round((marge / Number(data.inkoopPrijs)) * 100) : 0;

  const teamNamen = Array.isArray(team) ? team.map(u => u.naam || u.email).filter(Boolean) : [];

  const handleRdw = async () => {
    if (!data.kenteken.trim()) { toast.error(t('Vul eerst het kenteken in')); return; }
    setLoadingRdw(true);
    try {
      const r = await rdwLookup(data.kenteken);
      if (r && r.merk) {
        const bouwjaar = r.datumEersteToelating
          ? parseInt(String(r.datumEersteToelating).slice(0, 4))
          : data.bouwjaar;
        setData(d => ({
          ...d,
          merk: r.merk || d.merk,
          model: r.handelsbenaming || d.model,
          bouwjaar: bouwjaar || d.bouwjaar,
        }));
        toast.success(`RDW: ${r.merk} ${r.handelsbenaming || ''}`.trim(), {
          description: `${r.voertuigsoort || ''}${r.kleur ? ' · ' + r.kleur : ''}${bouwjaar ? ' · ' + bouwjaar : ''}`,
        });
      } else {
        toast.error(t('RDW lookup mislukt'));
      }
    } catch (e) {
      toast.error(t('RDW lookup mislukt'), { description: e.response?.data?.detail || e.message });
    } finally { setLoadingRdw(false); }
  };

  const handleVin = async () => {
    const vin = (data.chassisnummer || '').trim();
    if (!vin) { toast.error(t('Vul eerst het chassisnummer in', 'Vul eerst het chassisnummer in')); return; }
    setLoadingVin(true);
    try {
      const info = await vinLookup(vin);
      setData(d => ({
        ...d,
        merk: info.merk || d.merk,
        model: info.model || d.model,
        bouwjaar: info.bouwjaar || d.bouwjaar,
      }));
      toast.success(`VIN: ${info.merk} ${info.model}`.trim(), { description: [info.bouwjaar, info.brandstofRaw, info.carrosserie, info.productieland].filter(Boolean).join(' · ') });
    } catch (e) {
      toast.error(t('VIN lookup mislukt', 'VIN lookup mislukt'), { description: e.response?.data?.detail || e.message });
    } finally { setLoadingVin(false); }
  };

  const handleSave = async () => {
    if (!data.kenteken.trim()) { toast.error(t('Kenteken is verplicht')); return; }
    const payload = {
      ...data,
      kenteken: data.kenteken.toUpperCase().trim(),
      kmStand: Number(data.kmStand) || 0,
      inkoopPrijs: Number(data.inkoopPrijs) || 0,
      verkoopPrijs: Number(data.verkoopPrijs) || 0,
      bouwjaar: Number(data.bouwjaar) || data.bouwjaar,
      kostenposten: kostenposten.map(k => ({
        ...k,
        kosten: Number(k.kosten) || 0,
      })),
      titelChecklist,
      totaalKosten,
    };
    try {
      if (isNew) {
        await createHandelAuto(payload);
        toast.success(t('Auto opgeslagen'));
      } else {
        await updateHandelAuto(auto.id, payload);
        toast.success(t('Auto opgeslagen'));
      }
      onClose();
    } catch (e) {
      toast.error(t('Opslaan mislukt'), { description: e.response?.data?.detail || e.message });
    }
  };

  return (
    <div className="cs-modal-overlay" onClick={requestClose}>
      <div className="cs-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 760 }} data-testid="handel-auto-editor">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <h3 style={{ margin: 0 }}>{isNew ? t('Nieuwe handel auto', 'Nieuwe handel auto') : t('Auto bewerken', 'Auto bewerken')}</h3>
          <button className="cs-btn ghost" onClick={requestClose}><X size={14} /></button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div style={{ gridColumn: '1 / 3' }}>
            <label className="cs-section-title">{t('Kenteken')} *</label>
            <div style={{ display: 'flex', gap: 6 }}>
              <input data-testid="handel-kenteken" className="cs-input" value={data.kenteken}
                onChange={e => upd('kenteken', e.target.value.toUpperCase())}
                placeholder="XX-123-Y" style={{ fontFamily: 'ui-monospace, monospace', fontWeight: 600 }} />
              <button className="cs-btn" onClick={handleRdw} disabled={loadingRdw} title={t('RDW opzoeken')}>
                <RefreshCw size={14} className={loadingRdw ? 'spin' : ''} /> RDW
              </button>
              <ScanAutopapieren onResult={applyScan} testid="handel-scan" label={t('Scan papieren', 'Scan papieren')} />
            </div>
            <p style={{ fontSize: 11.5, color: 'var(--cs-text-muted)', margin: '4px 0 0' }}>
              {t('Geen NL-kenteken? Scan de (buitenlandse) autopapieren met AI.', 'Geen NL-kenteken? Scan de (buitenlandse) autopapieren met AI.')}
            </p>
          </div>
          <div style={{ gridColumn: '1 / 3' }}>
            <label className="cs-section-title">{t('Chassisnummer (VIN)', 'Chassisnummer (VIN)')} <span style={{ fontWeight: 400, textTransform: 'none', color: 'var(--cs-text-muted)' }}>— {t('handig bij buitenlandse auto\'s', 'handig bij buitenlandse auto\'s')}</span></label>
            <div style={{ display: 'flex', gap: 6 }}>
              <input data-testid="handel-chassisnummer" className="cs-input" value={data.chassisnummer || ''}
                onChange={e => upd('chassisnummer', e.target.value.toUpperCase())}
                placeholder="WVWZZZ1JZXW000001" maxLength={17} style={{ fontFamily: 'ui-monospace, monospace' }} />
              <button data-testid="handel-btn-vin" className="cs-btn" onClick={handleVin} disabled={loadingVin} title={t('Haal merk/model/bouwjaar op via chassisnummer', 'Haal merk/model/bouwjaar op via chassisnummer')}>
                <RefreshCw size={14} className={loadingVin ? 'spin' : ''} /> VIN
              </button>
            </div>
          </div>
          <div><label className="cs-section-title">{t('Merk')}</label><input className="cs-input" value={data.merk} onChange={e => upd('merk', e.target.value)} /></div>
          <div><label className="cs-section-title">{t('Model')}</label><input className="cs-input" value={data.model} onChange={e => upd('model', e.target.value)} /></div>
          <div><label className="cs-section-title">{t('Bouwjaar')}</label><input className="cs-input" type="number" value={data.bouwjaar} onChange={e => upd('bouwjaar', e.target.value)} /></div>
          <div><label className="cs-section-title">{t('KM stand')}</label><input className="cs-input" type="number" value={data.kmStand} onChange={e => upd('kmStand', e.target.value)} /></div>
          <div><label className="cs-section-title">{t('Inkoop')}prijs €</label><input data-testid="handel-inkoop" className="cs-input" type="number" step="0.01" value={data.inkoopPrijs} onChange={e => upd('inkoopPrijs', e.target.value)} /></div>
          <div><label className="cs-section-title">{t('Verkoop')}prijs €</label><input data-testid="handel-verkoop" className="cs-input" type="number" step="0.01" value={data.verkoopPrijs} onChange={e => upd('verkoopPrijs', e.target.value)} /></div>
          <div><label className="cs-section-title">{t('Ingekocht op')}</label><input className="cs-input" type="date" value={data.ingekochtOp} onChange={e => upd('ingekochtOp', e.target.value)} /></div>
          <div>
            <label className="cs-section-title">{t('Status')}</label>
            <select className="cs-input" value={data.status} onChange={e => {
              const v = e.target.value;
              setData(d => ({
                ...d,
                status: v,
                verkochtOp: v === 'Verkocht' ? (d.verkochtOp || new Date().toISOString().slice(0, 10)) : d.verkochtOp,
              }));
            }} data-testid="handel-status">
              {STATUSSEN.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          {data.status === 'Verkocht' && (
            <div>
              <label className="cs-section-title">{t('Verkocht op', 'Verkocht op')}</label>
              <input className="cs-input" type="date" value={data.verkochtOp || ''} onChange={e => upd('verkochtOp', e.target.value)} data-testid="handel-verkocht-op" />
            </div>
          )}
          <div style={{ gridColumn: '1 / 3' }}>
            <label className="cs-section-title">{t('Notitie')}</label>
            <textarea className="cs-input" rows={2} value={data.notitie} onChange={e => upd('notitie', e.target.value)} />
          </div>
        </div>

        {/* Kostenposten / vervangen onderdelen */}
        <div style={{ marginTop: 16, padding: 12, background: 'var(--cs-card-alt)', borderRadius: 8 }} data-testid="kostenposten-section">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <b style={{ fontSize: 13 }}>{t('Vervangen onderdelen / kosten')}</b>
            <button className="cs-btn primary" onClick={addKostenpost} data-testid="add-kostenpost" style={{ padding: '4px 10px', fontSize: 12 }}>
              <Plus size={12} /> {t('Toevoegen')}
            </button>
          </div>

          {kostenposten.length === 0 ? (
            <div style={{ fontSize: 12, color: 'var(--cs-text-muted)', textAlign: 'center', padding: 8 }}>
              {t('Nog geen kostenposten. Klik')} <b>+ {t('Toevoegen')}</b> {t('om bijvoorbeeld een vervangen onderdeel of voorgeschoten kosten te registreren.')}
            </div>
          ) : (
            <div style={{ display: 'grid', gap: 6 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '2.4fr 0.9fr 1.3fr 1.1fr 40px', gap: 6, fontSize: 11, color: 'var(--cs-text-muted)', textTransform: 'uppercase', fontWeight: 600 }}>
                <span>{t('Omschrijving / vervangen')}</span>
                <span>{t('Kosten')} €</span>
                <span>{t('Voorgeschoten door')}</span>
                <span>{t('Datum')}</span>
                <span />
              </div>
              {kostenposten.map(k => (
                <div key={k.id} style={{ display: 'grid', gridTemplateColumns: '2.4fr 0.9fr 1.3fr 1.1fr 40px', gap: 6, alignItems: 'center' }} data-testid={`kostenpost-row-${k.id}`}>
                  <input
                    className="cs-input"
                    placeholder={t('bv. Remblokken voor + olieservice')}
                    value={k.omschrijving}
                    onChange={e => updateKostenpost(k.id, { omschrijving: e.target.value })}
                    data-testid={`kostenpost-omschrijving-${k.id}`}
                  />
                  <input
                    className="cs-input"
                    type="number"
                    step="0.01"
                    value={k.kosten}
                    onChange={e => updateKostenpost(k.id, { kosten: e.target.value })}
                    data-testid={`kostenpost-kosten-${k.id}`}
                  />
                  <input
                    className="cs-input"
                    list={`team-namen-${k.id}`}
                    placeholder={t('bv. Jan / Garage')}
                    value={k.voorgeschotenDoor}
                    onChange={e => updateKostenpost(k.id, { voorgeschotenDoor: e.target.value })}
                    data-testid={`kostenpost-voorschot-${k.id}`}
                  />
                  <datalist id={`team-namen-${k.id}`}>
                    <option value="Garage" />
                    {teamNamen.map(n => <option key={n} value={n} />)}
                  </datalist>
                  <input
                    className="cs-input"
                    type="date"
                    value={k.datum}
                    onChange={e => updateKostenpost(k.id, { datum: e.target.value })}
                  />
                  <button onClick={() => removeKostenpost(k.id)} title={t('Verwijder kostenpost')} data-testid={`kostenpost-delete-${k.id}`}
                    style={{ width: 36, height: 34, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: 0, border: '1px solid var(--cs-border)', borderRadius: 6, background: '#fff', color: '#dc2626', cursor: 'pointer' }}>
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
              <div style={{ display: 'flex', justifyContent: 'flex-end', fontSize: 13, paddingTop: 6, borderTop: '1px dashed var(--cs-border)', marginTop: 4 }}>
                <span style={{ marginRight: 8, color: 'var(--cs-text-muted)' }}>{t('Totaal kosten:')}</span>
                <b data-testid="totaal-kosten">€ {totaalKosten.toLocaleString('nl-NL', { minimumFractionDigits: 2 })}</b>
              </div>
            </div>
          )}
        </div>

        {/* RDW-titelchecklist */}
        <div style={{ marginTop: 16, padding: 12, background: 'var(--cs-card-alt)', borderRadius: 8 }} data-testid="titel-checklist-section">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <b style={{ fontSize: 13 }}>{t('RDW-titelchecklist', 'RDW-titelchecklist')}</b>
            <span style={{ fontSize: 12, fontWeight: 700, color: titelChecklist.every(c => c.done) ? 'var(--cs-green)' : 'var(--cs-amber, #b45309)' }}>
              {titelChecklist.filter(c => c.done).length}/{titelChecklist.length}
            </span>
          </div>
          <div style={{ display: 'grid', gap: 4 }}>
            {titelChecklist.map(c => (
              <button key={c.key} type="button" onClick={() => toggleChecklist(c.key)} data-testid={`titel-check-${c.key}`}
                style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 6px', background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left', fontSize: 12.5, color: 'var(--cs-text)' }}>
                {c.done ? <CheckSquare size={16} style={{ color: 'var(--cs-green)', flexShrink: 0 }} /> : <Square size={16} style={{ color: 'var(--cs-text-muted)', flexShrink: 0 }} />}
                <span style={{ textDecoration: c.done ? 'none' : 'none', color: c.done ? 'var(--cs-text)' : 'var(--cs-text-muted)' }}>{c.label}</span>
              </button>
            ))}
          </div>
        </div>

        <div style={{ marginTop: 12, padding: 10, background: marge >= 0 ? 'var(--cs-green-soft)' : 'var(--cs-red-soft)', borderRadius: 6, fontSize: 13 }} data-testid="marge-block">
          <b>{t('Marge')}:</b> € {marge.toLocaleString('nl-NL', { minimumFractionDigits: 2 })} ({margePct}%)
          {totaalKosten > 0 && (
            <span style={{ marginLeft: 10, color: 'var(--cs-text-muted)', fontSize: 12 }}>
              = verkoop − inkoop − kosten (€ {totaalKosten.toLocaleString('nl-NL', { minimumFractionDigits: 2 })})
            </span>
          )}
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
          <button className="cs-btn" onClick={requestClose}>{t('Annuleren')}</button>
          <button data-testid="handel-auto-save" className="cs-btn primary" onClick={handleSave}><Save size={14} /> {t('Opslaan')}</button>
        </div>

        <style>{`.spin { animation: spin 1s linear infinite; } @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
      </div>
    </div>
  );
}
